import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { paymentsAPI, giftCardsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, Loader2, ArrowRight, Gift } from 'lucide-react';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const gcType = searchParams.get('type');
  const gcId = searchParams.get('gc_id');
  const groupId = searchParams.get('group_id');
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [gcData, setGcData] = useState(null);
  const polledRef = useRef(false);
  const gtmFiredRef = useRef(false);

  // GTM: push reservation_confirmee once, only for booking flows (not gift cards)
  const fireReservationConfirmee = (value, transactionId) => {
    if (gtmFiredRef.current) return;
    if (!transactionId || value == null) return;
    gtmFiredRef.current = true;
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'reservation_confirmee',
        value: Number(value),
        currency: 'EUR',
        transaction_id: transactionId,
      });
    } catch(e) { /* GTM optional */ }
  };

  useEffect(() => {
    if (polledRef.current) return;
    polledRef.current = true;

    // Booking via Revolut
    if (gcType === 'booking' && groupId) {
      let attempts = 0;
      const maxAttempts = 12;
      const poll = async () => {
        try {
          const res = await paymentsAPI.confirmRevolutBooking(groupId);
          const d = res.data;
          if (d.payment_status === 'paid') {
            fireReservationConfirmee(d.amount, groupId);
            setStatus('success');
            return;
          }
          if (d.payment_status === 'failed') { setStatus('error'); return; }
          attempts++;
          if (attempts < maxAttempts) setTimeout(poll, 2500);
          else setStatus('timeout');
        } catch {
          attempts++;
          if (attempts < maxAttempts) setTimeout(poll, 2500);
          else setStatus('error');
        }
      };
      poll();
      return;
    }

    // Gift card via Revolut
    if (gcType === 'gift_card' && gcId) {
      let attempts = 0;
      const maxAttempts = 10;
      const poll = async () => {
        try {
          const res = await giftCardsAPI.confirm(gcId);
          const d = res.data;
          if (d.status === 'paid' || d.status === 'already_active') {
            setGcData(d);
            setStatus('success');
            return;
          } else if (d.status === 'pending') {
            attempts++;
            if (attempts < maxAttempts) setTimeout(poll, 2500);
            else setStatus('timeout');
          } else {
            setStatus('error');
          }
        } catch {
          attempts++;
          if (attempts < maxAttempts) setTimeout(poll, 2500);
          else setStatus('error');
        }
      };
      poll();
      return;
    }

    // Regular Stripe payment
    if (!sessionId) { setStatus('error'); return; }
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      try {
        const res = await paymentsAPI.getStatus(sessionId);
        setPaymentData(res.data);
        if (res.data.payment_status === 'paid') {
          const meta = res.data.metadata || {};
          const isBooking = meta.type === 'booking' || meta.type === 'multi_booking';
          if (isBooking) {
            const amount = (res.data.amount_total || 0) / 100;
            const txId = meta.group_id || meta.booking_id || sessionId;
            fireReservationConfirmee(amount, txId);
          }
          setStatus('success');
          return;
        }
        else if (res.data.status === 'expired') { setStatus('expired'); return; }
        attempts++;
        if (attempts < maxAttempts) setTimeout(poll, 2000);
        else setStatus('timeout');
      } catch {
        attempts++;
        if (attempts < maxAttempts) setTimeout(poll, 2000);
        else setStatus('error');
      }
    };
    poll();
  }, [sessionId, gcType, gcId, groupId]);

  const isGiftCard = gcType === 'gift_card' || paymentData?.metadata?.type === 'gift_card';

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] px-6" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        {status === 'loading' && (
          <div className="animate-fade-in-up">
            <Loader2 className="w-16 h-16 mx-auto text-[#00FF00] animate-spin mb-6" />
            <h2 className="font-heading text-2xl font-bold text-white mb-2" data-testid="payment-processing">
              Vérification du paiement...
            </h2>
            <p className="text-slate-500">Merci de patienter quelques instants</p>
          </div>
        )}
        {status === 'success' && (
          <div className="animate-fade-in-up">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#00FF00]/15 flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(0,255,0,0.2)]">
              {isGiftCard ? <Gift className="w-10 h-10 text-[#00FF00]" /> : <CheckCircle2 className="w-10 h-10 text-[#00FF00]" />}
            </div>
            <h2 className="font-heading text-2xl font-bold text-white mb-2" data-testid="payment-success-title">
              {isGiftCard ? 'Carte cadeau activée !' : 'Paiement confirmé !'}
            </h2>
            {isGiftCard && gcData && (
              <div className="my-6">
                <div className="w-full aspect-[1.6/1] max-w-xs mx-auto rounded-2xl bg-[#00FF00] shadow-[0_0_40px_rgba(0,255,0,0.2)] flex flex-col justify-between p-5">
                  <div className="flex justify-between items-start">
                    <span className="font-heading text-xs font-bold text-black">RIDINGWATT</span>
                    <span className="font-heading text-[10px] font-bold text-black/50 uppercase">Carte Cadeau</span>
                  </div>
                  <div>
                    <p className="text-[10px] text-black/50 mb-1">Valeur</p>
                    <p className="font-heading text-3xl font-bold text-black">{gcData.amount}€</p>
                  </div>
                  <p className="font-mono text-xs text-black/70 tracking-wide" data-testid="gc-code-display">{gcData.code}</p>
                </div>
                <p className="text-sm text-slate-400 mt-4">Le PDF a été envoyé par email</p>
              </div>
            )}
            {!isGiftCard && (
              <p className="text-slate-400 mb-8">
                Votre réservation est confirmée. À bientôt sur l'eau !
              </p>
            )}
            <div className="flex flex-col gap-3">
              <Button onClick={() => navigate('/dashboard')}
                className="btn-pill bg-[#00FF00] text-black hover:bg-[#00CC00] shadow-[0_0_20px_rgba(0,255,0,0.3)] py-6 font-bold"
                data-testid="go-to-dashboard-btn">
                Mon espace <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button variant="ghost" onClick={() => navigate('/')}
                className="text-slate-500 hover:text-[#00FF00]"
                data-testid="back-to-home-btn">
                Retour à l'accueil
              </Button>
            </div>
          </div>
        )}
        {(status === 'expired' || status === 'error' || status === 'timeout') && (
          <div className="animate-fade-in-up">
            <div className="w-20 h-20 mx-auto rounded-full bg-red-500/15 flex items-center justify-center mb-6">
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-white mb-2" data-testid="payment-error-title">
              {status === 'expired' ? 'Session expirée' : 'Erreur de vérification'}
            </h2>
            <p className="text-slate-500 mb-8">
              {status === 'timeout'
                ? 'La vérification a pris trop de temps. Vérifiez votre email.'
                : 'Un problème est survenu. Contactez-nous si le montant a été débité.'}
            </p>
            <Button onClick={() => navigate('/')}
              className="btn-pill bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold"
              data-testid="return-home-btn">
              Retour à l'accueil
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
